import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import {
  EmitRettifica,
  Rettifica,
} from '../../../interfaces/rettifica.interface';
import { IdStatoRichiestaRettifica } from '../../enum/statoIdRettifica.enum';
import { BehaviorSubject, Subscription, switchMap } from 'rxjs';
import { SelectionModel } from '@angular/cdk/collections';
import {
  CurrentSort,
  IconTableEvent,
} from '../../../interfaces/table.interface';
import { FiltroService } from '../../../service/filtro.service';
import { RettificheService } from '../../../service/rettifiche.service';
import { LoaderService } from '../../../service/loader.service';
import { Attori } from '../../enum/attori.enum';
import { PaginationComponent } from '../pagination/pagination.component';
import { SharedModule } from '../../../core/shared.module';
import { AnagraficaTimesheet } from '../../../interfaces/timesheet.interface';
import { FiltroRiepilogoModifiche, filtroRiepilogoModificheVuoto } from '../../../interfaces/filtro-riepilogo-modifiche.interface';

@Component({
  selector: 'app-tabella-modifiche',
  standalone: true,
  imports: [SharedModule, PaginationComponent],
  templateUrl: './tabella-modifiche.component.html',
})
export class TabellaModificheComponent {
  @Input() attore: number = 0;
  @Input() isDettaglio: boolean = false;
  @Input() isVisualizzazioneRCD: boolean = false;
  @Input() anagraficaTimesheet: AnagraficaTimesheet | null = null;

  private _modifiche!: Rettifica[];
  @Input() set modifiche(value: Rettifica[]) {
    if (value) {
      this._modifiche = value;
      this.componentLoaded = true;
    }
  }

  get modifiche() {
    return this._modifiche;
  }

  @Input() hasSelection: boolean = false;
  @Input() selection!: SelectionModel<any>;
  @Input() editable: boolean = true;
  @Input() elencoCount: number = 0;

  @Output() updateModifica = new EventEmitter<EmitRettifica>();
  @Output() deleteModifica = new EventEmitter<EmitRettifica>();
  @Output() selectionItemEvent = new EventEmitter();
  @Output() iconItemEvent = new EventEmitter();

  idStatoRichiestaRettifica = IdStatoRichiestaRettifica;
  attori = Attori;
  currentSort: CurrentSort = { orderBy: '', flagAsc: false };
  totalPages: number = 0;
  currentPage: number = 0;
  pageOffset: number = 10;
  private readonly pageSubject = new BehaviorSubject<number>(0);
  pageSubscriptions: Subscription = new Subscription();
  form: any = {};
  componentLoaded: boolean = false;
  filtro: FiltroRiepilogoModifiche = filtroRiepilogoModificheVuoto;
  isCheckboxEnabled: boolean = false;

  constructor(
    private filtroService: FiltroService,
    private rettificheService: RettificheService,
    private loaderService: LoaderService,
    private cd: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.filtroService.ultimoFiltroRiepilogoModifiche(this.attore).subscribe((filtro) => {
      this.form = filtro;
    });
    if (this.filtroService.ultimaPaginaRiepilogoModificheInfo.pageOffset) {
      this.pageOffset = this.filtroService.ultimaPaginaRiepilogoModificheInfo.pageOffset;
    }
    if (this.filtroService.ultimaPaginaRiepilogoModificheInfo.currentPage) {
      this.currentPage = this.filtroService.ultimaPaginaRiepilogoModificheInfo.currentPage;
    }
    this.totalPages = Math.ceil(this.elencoCount / this.pageOffset);
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.modifiche.filter(
      (rettifica) =>
        rettifica.idStatoApprovazioneRichiestaRettifica ===
        IdStatoRichiestaRettifica.In_Approvazione
    ).length;
    return numSelected === numRows;
  }

  masterSelectionAll() {
    this.isAllSelected()
      ? this.selection.clear()
      : this.modifiche.forEach((row) => {
          if (
            row.idStatoApprovazioneRichiestaRettifica ==
            IdStatoRichiestaRettifica.In_Approvazione
          )
            this.selection.select(row);
        });
    this.selectionItemEvent.emit(this.selection);
  }

  masterSelection(item: any) {
    this.selection.toggle(item);
    this.selectionItemEvent.emit(this.selection);
  }

  onIconClick(item: Rettifica, columnDef: string) {
    const data: IconTableEvent = { item: item, columnDef: columnDef };
    this.iconItemEvent.emit(data);
  }

  exportDataToExcel(): void {
    this.loaderService.show();
    const filtro = { ...this.filtro };
    filtro.flagExport = true;
    this.pageSubscriptions.add(
      this.rettificheService
        .riepilogoRettifiche(filtro)
        .subscribe({
          next: (result) => {
            if (result) {
              const blob = new Blob([result], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              });
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = 'ElencoRichiesteModifiche.xlsx';
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              window.URL.revokeObjectURL(url);
            }
          },
          complete: () => this.loaderService.hide(),
          error: () => this.loaderService.hide(),
        })
    );
  }

  handleSearchEvent(event: any): void {
    this.form = event;
    this.getPage();
  }

  getPage(page: number = 0): void {
    this.currentPage = page;
    this.filtroService.setUltimaPaginaRiepilogoModificheInfo({
      currentPage: this.currentPage,
      pageOffset: this.pageOffset,
    });
    const convertEmptyStringsToNull = (obj: any) => {
      Object.keys(obj).forEach((key) => {
        if (obj[key] === '') {
          obj[key] = null;
        }
      });
      return obj;
    };

    const formValues = convertEmptyStringsToNull(this.form);
    this.filtro = {
      risorsa: formValues.risorsa,
      nomeRisorsa: formValues.nomeRisorsa,
      descrizioneUnitaOrganizzativaPrimoLivello:
        formValues.descrizioneUnitaOrganizzativaPrimoLivello,
      nomeApprovatore: formValues.nomeApprovatore,
      dataApprovazione: formValues.dataApprovazione,
      flagExport: false,
      pageNumber: page + 1,
      pageSize: this.pageOffset,
      flagAsc: this.currentSort.flagAsc,
      orderBy: this.currentSort.orderBy,
    };

    if (formValues.commessaSorgente?.id) {
      this.filtro.codiceCommessaSorgente = formValues.commessaSorgente.id;
      this.filtro.descrizioneCommessaSorgente = formValues.commessaSorgente.descrizione;
    } else {
      this.filtro.descrizioneCommessaSorgente = formValues.commessaSorgente;
    }
    if (formValues.commessaDestinazione?.id) {
      this.filtro.codiceCommessaDestinazione =
        formValues.commessaDestinazione.id;
      this.filtro.descrizioneCommessaDestinazione = formValues.commessaDestinazione.descrizione;
    } else {
      this.filtro.descrizioneCommessaDestinazione =
        formValues.commessaDestinazione;
    }

    this.filtroService.setUltimoFiltroRiepilogoModifiche(this.filtro, this.attore);
    this.loaderService.show();
    if (!this.isDettaglio) {
    this.pageSubscriptions.add(
      this.rettificheService
        .riepilogoRettifiche(this.filtro, true)
        .pipe(
          switchMap((count: number) => {
            this.elencoCount = count;
            this.totalPages = Math.ceil(this.elencoCount / this.pageOffset);
            return this.rettificheService.riepilogoRettifiche(
              this.filtro
            );
          })
        )
        .subscribe({
          next: (data: Rettifica[]) => {
            this.modifiche = data;
            this.cd.detectChanges();
          },
          complete: () => this.loaderService.hide(),
          error: () => this.loaderService.hide(),
        })
    );
    this.pageSubject.next(page);
    } else {
      this.pageSubscriptions.add(
        this.rettificheService
          .riepilogoRettifiche(this.filtro)
          .subscribe({
            next: (data: Rettifica[]) => {
              this.modifiche = data;
              this.cd.detectChanges();
            },
            complete: () => this.loaderService.hide(),
            error: () => this.loaderService.hide(),
          })
      );
    }
    this.componentLoaded = true;
  }

  changerEvent(value: number): void {
    this.pageOffset = value;
    this.totalPages = Math.ceil(this.elencoCount / this.pageOffset);
    this.currentPage = Math.min(this.currentPage, this.totalPages - 1);
    this.getPage(this.currentPage);
    this.pageSubject.next(this.currentPage);
  }

  ngOnDestroy(): void {
    this.pageSubscriptions.unsubscribe();
  }
}
